import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isTradingDay, nextTradingDay, toDateStr } from '../utils/tradingCalendar';

/**
 * A date picker that only allows selection of US market trading days.
 * Weekends and NYSE holidays are greyed-out and unclickable.
 *
 * Props:
 *   value    — "YYYY-MM-DD" string
 *   onChange — called with a "YYYY-MM-DD" string when a valid date is picked
 */
export default function TradingDatePicker({ value, onChange }) {
  const selected = value ? new Date(value + 'T12:00:00') : null;

  const handleChange = (date) => {
    if (!date) return;
    const yyyy = date.getFullYear();
    const mm   = String(date.getMonth() + 1).padStart(2, '0');
    const dd   = String(date.getDate()).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
  };

  // Button label: "Today" when today is a trading day, "Next Session" on holidays/weekends.
  const todayIsTrading = isTradingDay(new Date());
  const nextSession    = nextTradingDay(new Date());
  const nextSessionStr = toDateStr(nextSession);
  const todayBtnLabel  = todayIsTrading ? 'Today' : 'Next Session';

  return (
    <>
      <style>{`
        /* ─── Popper wrapper ─────────────────────────────────────────────────
           popperClassName="tdp-popper" adds the class to the outer popper div */
        .tdp-popper { z-index: 9999 !important; }

        /* ─── Calendar shell ─────────────────────────────────────────────────
           calendarClassName="tdp" adds "tdp" directly TO the .react-datepicker
           element — so we target it as .tdp (NO space / descendant combinator) */
        .tdp {
          background-color: #111827 !important;
          border: 1px solid #374151 !important;
          border-radius: 12px !important;
          font-family: inherit !important;
          font-size: 12px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.7) !important;
          overflow: hidden !important;
          color: #d1d5db !important;
        }

        /* ─── Header ─────────────────────────────────────────────────────── */
        .tdp .react-datepicker__header {
          background-color: #1f2937 !important;
          border-bottom: 1px solid #374151 !important;
          padding: 10px 0 6px !important;
        }
        .tdp .react-datepicker__current-month {
          color: #e5e7eb !important;
          font-size: 13px !important;
          font-weight: 600 !important;
        }

        /* ─── Month / year <select> dropdowns ───────────────────────────────
           Native <select> keeps browser defaults unless forced explicitly.    */
        .tdp .react-datepicker__month-select,
        .tdp .react-datepicker__year-select {
          background-color: #1f2937 !important;
          color: #e5e7eb !important;
          border: 1px solid #4b5563 !important;
          border-radius: 4px !important;
          padding: 2px 6px !important;
          font-size: 12px !important;
          font-family: inherit !important;
          cursor: pointer !important;
          outline: none !important;
        }
        .tdp .react-datepicker__month-select option,
        .tdp .react-datepicker__year-select option {
          background-color: #1f2937 !important;
          color: #e5e7eb !important;
        }
        .tdp .react-datepicker__month-select:focus,
        .tdp .react-datepicker__year-select:focus {
          border-color: #2563eb !important;
        }

        /* ─── Navigation arrows ─────────────────────────────────────────── */
        .tdp .react-datepicker__navigation-icon::before {
          border-color: #9ca3af !important;
        }
        .tdp .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
          border-color: #60a5fa !important;
        }

        /* ─── Day-of-week header row ────────────────────────────────────────
           ALIGNMENT KEY: day-name and day cells must share identical
           width, margin, display, and text-align so columns stay in sync.    */
        .tdp .react-datepicker__day-names {
          background-color: #1f2937 !important;
          display: flex !important;
          justify-content: center !important;
          padding: 0 8px !important;
        }
        .tdp .react-datepicker__day-name {
          color: #6b7280 !important;
          font-size: 11px !important;
          font-weight: 500 !important;
          width: 2rem !important;
          line-height: 2rem !important;
          text-align: center !important;
          margin: 0 2px !important;
          display: inline-block !important;
          box-sizing: border-box !important;
        }

        /* ─── Month body ────────────────────────────────────────────────── */
        .tdp .react-datepicker__month {
          background-color: #111827 !important;
          margin: 6px 8px !important;
        }
        .tdp .react-datepicker__week {
          display: flex !important;
          justify-content: center !important;
        }

        /* ─── Every day cell ────────────────────────────────────────────────
           Must match day-name: width 2rem, margin 0 2px, text-align center.
           -webkit-text-fill-color overrides iOS Safari's system text colour. */
        .tdp .react-datepicker__day {
          background-color: transparent !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          width: 2rem !important;
          line-height: 2rem !important;
          text-align: center !important;
          margin: 0 2px !important;
          border-radius: 6px !important;
          display: inline-block !important;
          box-sizing: border-box !important;
          transition: background 0.12s !important;
          font-size: 12px !important;
        }

        /* Hover on selectable days */
        .tdp .react-datepicker__day:hover:not(.react-datepicker__day--disabled) {
          background-color: #1d4ed8 !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }

        /* ─── Selected / keyboard-highlighted ───────────────────────────── */
        .tdp .react-datepicker__day--selected,
        .tdp .react-datepicker__day--keyboard-selected {
          background-color: #2563eb !important;
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          font-weight: 700 !important;
        }

        /* ─── Today — subtle blue ring ──────────────────────────────────── */
        .tdp .react-datepicker__day--today:not(.react-datepicker__day--selected) {
          border: 1px solid #2563eb !important;
          color: #60a5fa !important;
          -webkit-text-fill-color: #60a5fa !important;
          font-weight: 600 !important;
          line-height: calc(2rem - 2px) !important;
        }

        /* ─── Disabled (weekends + holidays) — matte dark ───────────────── */
        .tdp .react-datepicker__day--disabled,
        .tdp .react-datepicker__day--disabled:hover {
          background-color: transparent !important;
          color: #4b5563 !important;
          -webkit-text-fill-color: #4b5563 !important;
          text-decoration: line-through !important;
          cursor: not-allowed !important;
          opacity: 1 !important;
        }

        /* ─── Days outside the visible month ────────────────────────────── */
        .tdp .react-datepicker__day--outside-month {
          color: #374151 !important;
          -webkit-text-fill-color: #374151 !important;
        }
        .tdp .react-datepicker__day--outside-month.react-datepicker__day--disabled {
          color: #1f2937 !important;
          -webkit-text-fill-color: #1f2937 !important;
        }

        /* ─── Triangle arrow ────────────────────────────────────────────── */
        .tdp .react-datepicker__triangle::before {
          border-bottom-color: #374151 !important;
        }
        .tdp .react-datepicker__triangle::after {
          border-bottom-color: #111827 !important;
        }
      `}</style>

      <DatePicker
        selected={selected}
        onChange={handleChange}
        filterDate={isTradingDay}
        dateFormat="MM/dd/yyyy"
        calendarClassName="tdp"
        popperClassName="tdp-popper"
        customInput={
          <input
            readOnly
            className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500 cursor-pointer"
            style={{
              backgroundColor: '#1f2937',
              color: '#e5e7eb',
              borderColor: '#374151',
              width: '112px',
            }}
          />
        }
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        popperPlacement="bottom-start"
      >
        {/* Today / Next Session button at the bottom of the calendar */}
        <div style={{ padding: '6px 8px 8px', borderTop: '1px solid #374151', textAlign: 'center' }}>
          <button
            onClick={() => onChange(nextSessionStr)}
            style={{
              width: '100%',
              padding: '5px 0',
              background: '#1d4ed8',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {todayBtnLabel}
          </button>
        </div>
      </DatePicker>
    </>
  );
}
